import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import { EyeSlashIcon } from "@phosphor-icons/react/dist/icons/EyeSlash";
import { EyeIcon } from "@phosphor-icons/react/dist/ssr/Eye";
import { Controller, useForm } from "react-hook-form";
import { z as zod } from "zod";

import { useUpdateUserProfile } from "@/hooks/api/use-users";
import { useAuth } from "@/components/core/auth-provider";
import { CustomDrawer } from "@/components/custom-drawer";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

export interface UserProfileDrawerProps {
	isUserProfileDrawerOpen: boolean;
	onUserProfileDrawerClose: () => void;
}

const schema = zod.object({
	name: zod.string().min(1, "Name is required"),
	password: zod.string().optional(),
	passwordConfirmation: zod.string().optional(),
});

type Values = zod.infer<typeof schema>;

export function UserProfileDrawer({
	isUserProfileDrawerOpen,
	onUserProfileDrawerClose,
}: UserProfileDrawerProps): React.JSX.Element {
	const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);
	const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = React.useState(false);

	const updateUserProfileMutation = useUpdateUserProfile();
	const { user, setUser } = useAuth();

	const defaultValues = {
		name: user?.name || "",
		password: "",
		passwordConfirmation: "",
	} satisfies Values;

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
		setError,
	} = useForm<Values>({
		defaultValues,
		resolver: zodResolver(schema),
	});

	React.useLayoutEffect(() => {
		reset({
			name: user?.name || "",
			password: "",
			passwordConfirmation: "",
		});
	}, [reset, user, isUserProfileDrawerOpen]);

	const onSubmitForm = async (data: Values) => {
		try {
			const formattedData = {
				name: data.name,
				...(data.password && { password: data.password }),
				...(data.passwordConfirmation && { passwordConfirmation: data.passwordConfirmation }),
			};

			const response = await updateUserProfileMutation.mutateAsync(formattedData);
			setUser(response);

			onUserProfileDrawerClose();
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;

			if (!Array.isArray(errorMessages)) {
				setError("root", { message: errorMessages });
				return;
			}

			const errorMessage = errorMessages[0];
			setError(errorMessage.split(" ")[0], { message: errorMessage });
		}
	};
	return (
		<CustomDrawer
			open={isUserProfileDrawerOpen}
			onClose={onUserProfileDrawerClose}
			title="Profile"
			onSubmit={handleSubmit(onSubmitForm)}
			isSubmitButtonDisabled={isSubmitting || updateUserProfileMutation.isPending}
			submitButtonText="Update Profile"
			width="420px"
		>
			<form onSubmit={handleSubmit(onSubmitForm)}>
				<Grid container spacing={2}>
					<Grid
						size={{
							md: 12,
							xs: 12,
						}}
					>
						<Controller
							control={control}
							name="name"
							render={({ field }) => (
								<FormControl error={Boolean(errors.name)} fullWidth>
									<InputLabel>Name</InputLabel>
									<OutlinedInput {...field} placeholder="Enter name" />
									{errors.name ? <CustomFormHelperError text={errors.name.message || ""} /> : null}
								</FormControl>
							)}
						/>
					</Grid>
					<Grid
						size={{
							md: 12,
							xs: 12,
						}}
					>
						<Controller
							control={control}
							name="password"
							render={({ field }) => (
								<FormControl error={Boolean(errors.password)} fullWidth>
									<InputLabel>Password</InputLabel>
									<OutlinedInput
										{...field}
										placeholder="Enter password"
										type={isPasswordVisible ? "text" : "password"}
										endAdornment={
											isPasswordVisible ? (
												<EyeIcon
													cursor="pointer"
													fontSize="var(--icon-fontSize-md)"
													onClick={(): void => {
														setIsPasswordVisible(false);
													}}
												/>
											) : (
												<EyeSlashIcon
													cursor="pointer"
													fontSize="var(--icon-fontSize-md)"
													onClick={(): void => {
														setIsPasswordVisible(true);
													}}
												/>
											)
										}
									/>
									{errors.password ? <CustomFormHelperError text={errors.password.message || ""} /> : null}
								</FormControl>
							)}
						/>
					</Grid>
					<Grid
						size={{
							md: 12,
							xs: 12,
						}}
					>
						<Controller
							control={control}
							name="passwordConfirmation"
							render={({ field }) => (
								<FormControl error={Boolean(errors.passwordConfirmation)} fullWidth>
									<InputLabel>Confirm Password</InputLabel>
									<OutlinedInput
										{...field}
										placeholder="Enter confirm password"
										type={isConfirmPasswordVisible ? "text" : "password"}
										endAdornment={
											isConfirmPasswordVisible ? (
												<EyeIcon
													cursor="pointer"
													fontSize="var(--icon-fontSize-md)"
													onClick={(): void => {
														setIsConfirmPasswordVisible(false);
													}}
												/>
											) : (
												<EyeSlashIcon
													cursor="pointer"
													fontSize="var(--icon-fontSize-md)"
													onClick={(): void => {
														setIsConfirmPasswordVisible(true);
													}}
												/>
											)
										}
									/>
									{errors.passwordConfirmation ? (
										<CustomFormHelperError text={errors.passwordConfirmation.message || ""} />
									) : null}
								</FormControl>
							)}
						/>
					</Grid>
				</Grid>
			</form>
		</CustomDrawer>
	);
}
